import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Divider,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import SaveIcon from '@material-ui/icons/Save';
import DirectionsBusIcon from '@material-ui/icons/DirectionsBus';
import PersonIcon from '@material-ui/icons/Person';
import RouteIcon from '@material-ui/icons/Timeline';
import { useNavigate } from 'react-router-dom';
import { firestore } from '../../firebase/config';
import { collection, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';

import PageHeader from '../../components/ui/PageHeader';

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionHead: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.palette.primary.light}`,
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(2),
  },
  infoBox: {
    background: 'linear-gradient(135deg, rgba(99,102,241,0.06), rgba(236,72,153,0.06))',
    border: '1px solid rgba(99,102,241,0.15)',
    borderRadius: 10,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const toLocalDatetimeValue = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const now = () => new Date();
const inHours = (h) => new Date(now().getTime() + h * 3600 * 1000);

const TripCreatePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [loadingData, setLoadingData] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [form, setForm] = useState({
    routeId: '',
    departureDate: toLocalDatetimeValue(inHours(1)),
    arrivalDate: toLocalDatetimeValue(inHours(4)),
    vehicleId: '',
    driverId: '',
    price: '',
    status: 'scheduled',
    notes: '',
  });
  const [errors, setErrors] = useState({});

  const fetchData = useCallback(async () => {
    setLoadingData(true);
    try {
      const [routesSnap, vehiclesSnap, driversSnap] = await Promise.all([
        getDocs(collection(firestore, 'routes')),
        getDocs(collection(firestore, 'vehicles')),
        getDocs(collection(firestore, 'drivers')),
      ]);

      const mapDoc = (d) => ({ id: d.id, ...d.data() });

      setRoutes(
        routesSnap.empty
          ? [
              { id: 'r1', name: 'Abidjan → Bouaké', origin: 'Abidjan', destination: 'Bouaké', distance: 350, duration: 240, price: 5000 },
              { id: 'r2', name: 'Abidjan → Yamoussoukro', origin: 'Abidjan', destination: 'Yamoussoukro', distance: 248, duration: 180, price: 3500 },
              { id: 'r3', name: 'Abidjan → San Pedro', origin: 'Abidjan', destination: 'San Pedro', distance: 340, duration: 300, price: 6000 },
            ]
          : routesSnap.docs.map(mapDoc).filter((r) => r.status !== 'inactive')
      );

      setVehicles(
        vehiclesSnap.empty
          ? [
              { id: 'v1', name: 'Bus 001', type: 'Bus', capacity: 50, licensePlate: 'CI-0001-A' },
              { id: 'v2', name: 'Bus 002', type: 'Bus', capacity: 45, licensePlate: 'CI-0002-A' },
              { id: 'v3', name: 'Minibus 003', type: 'Minibus', capacity: 22, licensePlate: 'CI-0003-A' },
            ]
          : vehiclesSnap.docs.map(mapDoc).filter((v) => v.status !== 'maintenance')
      );

      setDrivers(
        driversSnap.empty
          ? [
              { id: 'd1', name: 'Kouamé Jean', licenseNumber: 'DRV001' },
              { id: 'd2', name: 'Koné Marie', licenseNumber: 'DRV002' },
              { id: 'd3', name: 'Diallo Pierre', licenseNumber: 'DRV003' },
            ]
          : driversSnap.docs.map(mapDoc).filter((d) => d.status !== 'unavailable')
      );
    } catch (err) {
      console.error('fetchData error:', err);
      setSnackbar({ open: true, message: 'Erreur lors du chargement des données', severity: 'error' });
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };

      if (name === 'routeId') {
        const route = routes.find((r) => r.id === value);
        if (route) {
          next.price = route.price ? String(route.price) : prev.price;
          if (prev.departureDate) {
            const dep = new Date(prev.departureDate);
            if (!Number.isNaN(dep.getTime()) && route.duration) {
              next.arrivalDate = toLocalDatetimeValue(
                new Date(dep.getTime() + route.duration * 60 * 1000)
              );
            }
          }
        }
      }

      if (name === 'departureDate') {
        const route = routes.find((r) => r.id === prev.routeId);
        if (route?.duration) {
          const dep = new Date(value);
          if (!Number.isNaN(dep.getTime())) {
            next.arrivalDate = toLocalDatetimeValue(
              new Date(dep.getTime() + route.duration * 60 * 1000)
            );
          }
        }
      }

      return next;
    });

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.routeId) e.routeId = "L'itinéraire est requis";
    if (!form.vehicleId) e.vehicleId = 'Le véhicule est requis';
    if (!form.driverId) e.driverId = 'Le chauffeur est requis';
    if (!form.departureDate) e.departureDate = 'La date de départ est requise';
    else if (new Date(form.departureDate) < now())
      e.departureDate = 'La date de départ doit être dans le futur';
    if (!form.arrivalDate) e.arrivalDate = "La date d'arrivée est requise";
    else if (form.departureDate && new Date(form.arrivalDate) <= new Date(form.departureDate))
      e.arrivalDate = "L'arrivée doit être après le départ";
    if (!form.price) e.price = 'Le prix est requis';
    else if (isNaN(form.price) || parseFloat(form.price) < 0)
      e.price = 'Prix invalide';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const route = routes.find((r) => r.id === form.routeId);
      const vehicle = vehicles.find((v) => v.id === form.vehicleId);
      const driver = drivers.find((d) => d.id === form.driverId);

      await addDoc(collection(firestore, 'trips'), {
        routeId: form.routeId,
        routeName: route?.name || '',
        origin: route?.origin || '',
        destination: route?.destination || '',
        departureDate: new Date(form.departureDate),
        arrivalDate: new Date(form.arrivalDate),
        vehicleId: form.vehicleId,
        vehicleName: vehicle?.name || '',
        vehiclePlate: vehicle?.licensePlate || '',
        driverId: form.driverId,
        driverName: driver?.name || '',
        price: parseFloat(form.price),
        status: form.status,
        notes: form.notes,
        totalSeats: vehicle?.capacity || 0,
        availableSeats: vehicle?.capacity || 0,
        bookedSeats: 0,
        createdAt: serverTimestamp(),
      });

      setSnackbar({ open: true, message: 'Voyage créé avec succès', severity: 'success' });
      setTimeout(() => navigate('/admin/trips'), 1400);
    } catch (err) {
      console.error('Error creating trip:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la création du voyage', severity: 'error' });
      setSubmitting(false);
    }
  };

  const selectedRoute = routes.find((r) => r.id === form.routeId);
  const selectedVehicle = vehicles.find((v) => v.id === form.vehicleId);

  return (
    <Box className="fade-in-up">
      <PageHeader
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Voyages', to: '/admin/trips' },
          { label: 'Nouveau voyage' },
        ]}
        title="Nouveau voyage"
        subtitle="Planifier un voyage et l'assigner à un véhicule et chauffeur"
      />

      {loadingData ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : (
        <form onSubmit={handleSubmit}>
          {/* Route & Schedule */}
          <Paper style={{ padding: 24, marginBottom: 20 }}>
            <Box className={classes.sectionHead}>
              <RouteIcon fontSize="small" />
              <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                Itinéraire & Horaires
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl variant="outlined" fullWidth error={!!errors.routeId}>
                  <InputLabel>Itinéraire</InputLabel>
                  <Select
                    name="routeId"
                    value={form.routeId}
                    onChange={handleChange}
                    label="Itinéraire"
                  >
                    {routes.map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.routeId && <FormHelperText>{errors.routeId}</FormHelperText>}
                </FormControl>

                {selectedRoute && (
                  <Box className={classes.infoBox} style={{ marginTop: 12 }}>
                    <Typography variant="caption" color="textSecondary">
                      <strong>Distance:</strong> {selectedRoute.distance} km &nbsp;|&nbsp;
                      <strong>Durée estimée:</strong> {Math.floor(selectedRoute.duration / 60)}h{selectedRoute.duration % 60 > 0 ? String(selectedRoute.duration % 60).padStart(2, '0') : ''} &nbsp;|&nbsp;
                      <strong>Prix suggéré:</strong> {selectedRoute.price?.toLocaleString('fr-FR')} FCFA
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date et heure de départ"
                  name="departureDate"
                  type="datetime-local"
                  variant="outlined"
                  fullWidth
                  required
                  value={form.departureDate}
                  onChange={handleChange}
                  error={!!errors.departureDate}
                  helperText={errors.departureDate}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date et heure d'arrivée"
                  name="arrivalDate"
                  type="datetime-local"
                  variant="outlined"
                  fullWidth
                  required
                  value={form.arrivalDate}
                  onChange={handleChange}
                  error={!!errors.arrivalDate}
                  helperText={errors.arrivalDate}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Vehicle & Driver */}
          <Paper style={{ padding: 24, marginBottom: 20 }}>
            <Box className={classes.sectionHead}>
              <DirectionsBusIcon fontSize="small" />
              <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                Véhicule & Chauffeur
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl variant="outlined" fullWidth error={!!errors.vehicleId}>
                  <InputLabel>Véhicule</InputLabel>
                  <Select
                    name="vehicleId"
                    value={form.vehicleId}
                    onChange={handleChange}
                    label="Véhicule"
                  >
                    {vehicles.map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                        {v.name} — {v.type}, {v.capacity} places ({v.licensePlate})
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.vehicleId && <FormHelperText>{errors.vehicleId}</FormHelperText>}
                </FormControl>
                {selectedVehicle && (
                  <Box className={classes.infoBox} style={{ marginTop: 8 }}>
                    <Typography variant="caption" color="textSecondary">
                      <strong>Capacité:</strong> {selectedVehicle.capacity} places
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl variant="outlined" fullWidth error={!!errors.driverId}>
                  <InputLabel>Chauffeur</InputLabel>
                  <Select
                    name="driverId"
                    value={form.driverId}
                    onChange={handleChange}
                    label="Chauffeur"
                  >
                    {drivers.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name}
                        {d.licenseNumber ? ` — ${d.licenseNumber}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.driverId && <FormHelperText>{errors.driverId}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Pricing & Status */}
          <Paper style={{ padding: 24, marginBottom: 20 }}>
            <Box className={classes.sectionHead}>
              <PersonIcon fontSize="small" />
              <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                Tarification & Statut
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Prix par passager"
                  name="price"
                  type="number"
                  variant="outlined"
                  fullWidth
                  required
                  value={form.price}
                  onChange={handleChange}
                  error={!!errors.price}
                  helperText={errors.price}
                  inputProps={{ min: 0, step: 50 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">FCFA</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    label="Statut"
                  >
                    <MenuItem value="scheduled">Programmé</MenuItem>
                    <MenuItem value="in_progress">En cours</MenuItem>
                    <MenuItem value="completed">Terminé</MenuItem>
                    <MenuItem value="cancelled">Annulé</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Notes / Informations supplémentaires"
                  name="notes"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Informations supplémentaires, conditions spéciales..."
                />
              </Grid>
            </Grid>

            <Divider style={{ margin: '16px 0' }} />

            <Box className={classes.actions}>
              <Button variant="outlined" onClick={() => navigate('/admin/trips')}>
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                disabled={submitting}
              >
                {submitting ? 'Création en cours…' : 'Créer le voyage'}
              </Button>
            </Box>
          </Paper>
        </form>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TripCreatePage;
